import { Plus, Trash2 } from 'lucide-react';
import type { ApprovalMode, DocumentTypeApproverInput } from '@/api/client';

export const APPROVER_ROLES = [
  'principal_investigator',
  'sub_investigator',
  'coordinator',
  'regulatory_specialist',
  'site_admin',
  'site_pharma',
  'site_study_designer',
  'monitor',
  'auditor',
  'irb_administrator',
  'compliance',
  'data_manager',
  'platform_admin',
] as const;

export const DEFAULT_SIGNATURE_MEANINGS: Record<string, string> = {
  principal_investigator: 'I approve this document for use at my site.',
  sub_investigator: 'I have reviewed and concur with this document.',
  coordinator: 'I prepared and verified this document.',
  regulatory_specialist: 'I have reviewed this document for regulatory compliance.',
  site_admin: 'I authorize this document for site use.',
  site_pharma: 'I have reviewed the IP-related content of this document.',
  monitor: 'I have monitored this document.',
  auditor: 'I have audited this document.',
  irb_administrator: 'I confirm IRB approval of this document.',
};

export function newApproverRow(
  mode: ApprovalMode,
  existing: DocumentTypeApproverInput[],
): DocumentTypeApproverInput {
  const order = existing.length === 0
    ? 1
    : mode === 'sequential'
    ? Math.max(...existing.map((a) => a.sequence_order)) + 1
    : Math.min(...existing.map((a) => a.sequence_order));
  return {
    role: 'principal_investigator',
    sequence_order: order,
    signature_meaning: DEFAULT_SIGNATURE_MEANINGS.principal_investigator ?? '',
  };
}

export function FolderApproverPicker({
  approvers,
  mode,
  disabled,
  onChange,
}: {
  approvers: DocumentTypeApproverInput[];
  mode: ApprovalMode;
  disabled: boolean;
  onChange: (next: DocumentTypeApproverInput[]) => void;
}) {
  const canAdd = mode !== 'single' || approvers.length === 0;

  function update(i: number, patch: Partial<DocumentTypeApproverInput>) {
    onChange(approvers.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
  }

  return (
    <div className="space-y-2">
      {approvers.length === 0 && (
        <p className="rounded border border-dashed border-surface-border bg-surface-muted/30 p-3 text-xs text-ink-muted">
          No approvers yet. Add the {mode === 'single' ? 'one person' : 'people'} who must sign
          before a document in this folder goes live.
        </p>
      )}

      {approvers.map((a, i) => (
        <div
          key={i}
          className="grid grid-cols-12 items-start gap-2 rounded border border-surface-border bg-surface-muted/20 p-2"
        >
          <div className="col-span-5">
            <div className="mb-0.5 text-[10px] uppercase tracking-wider text-ink-muted">Role</div>
            <select
              value={a.role}
              disabled={disabled}
              onChange={(e) => {
                const role = e.target.value;
                const wasDefault =
                  !a.signature_meaning
                  || a.signature_meaning === DEFAULT_SIGNATURE_MEANINGS[a.role];
                update(i, {
                  role,
                  ...(wasDefault
                    ? { signature_meaning: DEFAULT_SIGNATURE_MEANINGS[role] ?? '' }
                    : {}),
                });
              }}
              className="h-8 w-full rounded border border-surface-border bg-surface-card px-1.5 text-xs"
            >
              {APPROVER_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>

          {mode === 'sequential' && (
            <div className="col-span-2">
              <div className="mb-0.5 text-[10px] uppercase tracking-wider text-ink-muted">
                Order
              </div>
              <input
                type="number"
                min={1}
                value={a.sequence_order}
                disabled={disabled}
                onChange={(e) =>
                  update(i, { sequence_order: Math.max(1, parseInt(e.target.value, 10) || 1) })
                }
                className="h-8 w-full rounded border border-surface-border bg-surface-card px-1.5 text-xs"
              />
            </div>
          )}

          <div className={mode === 'sequential' ? 'col-span-4' : 'col-span-6'}>
            <div className="mb-0.5 text-[10px] uppercase tracking-wider text-ink-muted">
              Meaning of signature
            </div>
            <input
              type="text"
              value={a.signature_meaning}
              disabled={disabled}
              maxLength={255}
              onChange={(e) => update(i, { signature_meaning: e.target.value })}
              className="h-8 w-full rounded border border-surface-border bg-surface-card px-1.5 text-xs"
            />
          </div>

          <div className="col-span-1 flex items-end justify-end pt-4">
            {!disabled && (
              <button
                type="button"
                onClick={() => onChange(approvers.filter((_, idx) => idx !== i))}
                className="rounded p-1 text-danger-600 hover:bg-danger-50"
                title="Remove this approver"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      ))}

      {!disabled && canAdd && (
        <button
          type="button"
          onClick={() => onChange([...approvers, newApproverRow(mode, approvers)])}
          className="btn-secondary h-8 text-xs"
        >
          <Plus className="h-3.5 w-3.5" /> Add approver
        </button>
      )}
    </div>
  );
}
